import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Briefcase, Star, Camera, MessageSquare, Share2, Zap, 
  ArrowRight, Check, Users, TrendingUp, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MeuTrampoDemo = () => {
  const navigate = useNavigate();

  const benefits = [
    { icon: Camera, title: 'Galeria de trabalhos', desc: 'Mostre seus melhores projetos com fotos antes e depois' },
    { icon: Star, title: 'Depoimentos reais', desc: 'Colete avaliações de clientes automaticamente' },
    { icon: Share2, title: 'Link único', desc: 'Compartilhe seu portfólio em qualquer lugar' },
    { icon: MessageSquare, title: 'WhatsApp integrado', desc: 'Clientes entram em contato direto' }
  ];

  const stats = [
    { value: '3x', label: 'mais contatos' },
    { value: '85%', label: 'fecham negócio' },
    { value: '24h', label: 'no ar sempre' }
  ];

  const useCases = [
    { icon: '🔨', title: 'Construção', examples: 'Pedreiros, pintores, eletricistas' },
    { icon: '✂️', title: 'Beleza', examples: 'Cabeleireiros, manicures, maquiadores' },
    { icon: '💻', title: 'Tecnologia', examples: 'Desenvolvedores, designers, social media' },
    { icon: '💪', title: 'Saúde', examples: 'Personal trainers, nutricionistas' },
    { icon: '📚', title: 'Educação', examples: 'Professores, mentores, coaches' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-purple-100 text-sm mb-6">
            <Award size={16} />
            Seu portfólio profissional em minutos
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Mostre seu trabalho.<br />
            <span className="text-purple-200">Conquiste mais clientes.</span>
          </h1>
          
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Crie um portfólio online profissional com galeria de trabalhos, 
            depoimentos de clientes e WhatsApp integrado.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              onClick={() => navigate('/meutrampo/configurar')}
              className="bg-white text-purple-700 hover:bg-purple-50 text-lg px-8 py-6"
            >
              <Zap size={20} />
              Criar meu portfólio
              <ArrowRight size={20} />
            </Button>
            <Button
              onClick={() => navigate('/meutrampo/joao-reformas')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6"
            >
              Ver exemplo
            </Button>
          </div>
          
          {/* Stats */}
          <div className="flex justify-center gap-8 sm:gap-16">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl sm:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-purple-200 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Seu portfólio, sua vitrine digital
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Um link único que você compartilha no WhatsApp, Instagram, cartão de visita... 
              e seus clientes veem todo seu trabalho.
            </p>
          </div>
          
          {/* Mockup do portfólio */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md mx-auto">
            <div className="h-32 bg-gradient-to-r from-orange-500 to-amber-500 relative">
              <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
                <img 
                  src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face"
                  alt="Foto"
                  className="w-24 h-24 rounded-full border-4 border-white object-cover"
                />
              </div>
            </div>
            <div className="pt-16 pb-6 px-6 text-center">
              <h3 className="text-xl font-bold text-slate-900">João Reformas</h3>
              <p className="text-slate-500 text-sm mb-4">Volta Redonda, RJ • 15 anos de experiência</p>
              
              <div className="flex justify-center gap-4 mb-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">200+</p>
                  <p className="text-xs text-slate-500">Projetos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">5.0</p>
                  <p className="text-xs text-slate-500">Avaliação</p>
                </div>
              </div>
              
              <div className="flex gap-2 justify-center mb-4">
                {['Reforma', 'Pintura', 'Elétrica'].map((cat, i) => (
                  <span key={i} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                    {cat}
                  </span>
                ))}
              </div>
              
              <Button className="w-full bg-green-500 hover:bg-green-600">
                <MessageSquare size={18} />
                Chamar no WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Tudo que você precisa para se destacar
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => (
              <div key={i} className="p-6 bg-white rounded-xl border border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                  <benefit.icon size={24} className="text-purple-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Para todo tipo de profissional
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {useCases.map((useCase, i) => (
              <div key={i} className="p-4 bg-white rounded-xl border border-slate-200 text-center hover:border-purple-300 transition-all">
                <span className="text-3xl mb-2 block">{useCase.icon}</span>
                <h3 className="font-semibold text-slate-900 mb-1">{useCase.title}</h3>
                <p className="text-slate-500 text-xs">{useCase.examples}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Collection Feature */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-3xl p-8 sm:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4">
                  Colete depoimentos automaticamente
                </h2>
                <p className="text-purple-100 mb-6">
                  Após cada trabalho, envie um link para seu cliente avaliar. 
                  Os depoimentos aparecem direto no seu portfólio.
                </p>
                <ul className="space-y-3">
                  {[
                    'Link único para cada cliente',
                    'Foto e texto do depoimento',
                    'Avaliação com estrelas',
                    'Aparece automático no portfólio'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check size={18} className="text-purple-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white rounded-xl p-6 text-slate-900">
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&h=50&fit=crop&crop=face"
                    alt="Cliente"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold">Maria Santos</p>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-slate-600 text-sm italic">
                  "Excelente profissional! Fez a reforma do meu apartamento com muito cuidado 
                  e no prazo combinado. Super recomendo!"
                </p>
                <p className="text-slate-400 text-xs mt-3">Reforma completa • Dezembro 2024</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Simples e acessível
          </h2>
          
          <div className="bg-white rounded-2xl border-2 border-purple-500 p-8 shadow-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm mb-4">
              <Briefcase size={14} />
              Meu Trampo
            </div>
            
            <div className="mb-6">
              <span className="text-5xl font-bold text-slate-900">R$ 39,99</span>
              <span className="text-slate-500">/mês</span>
            </div>
            
            <ul className="space-y-3 text-left mb-8">
              {[
                'Portfólio online ilimitado',
                'Galeria de trabalhos',
                'Coleta de depoimentos',
                'WhatsApp integrado',
                'Link personalizado',
                'Estatísticas de visitas'
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-700">
                  <Check size={18} className="text-purple-500" />
                  {item}
                </li>
              ))}
            </ul>
            
            <Button
              onClick={() => navigate('/meutrampo/configurar')}
              className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6"
            >
              Começar agora
              <ArrowRight size={20} />
            </Button>
            
            <p className="text-slate-500 text-sm mt-4">
              Teste grátis • Cancele quando quiser
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Pronto para mostrar seu trabalho?
          </h2>
          <p className="text-slate-600 mb-8">
            Em poucos minutos você terá um portfólio profissional online.
          </p>
          <Button
            onClick={() => navigate('/meutrampo/configurar')}
            className="bg-purple-600 hover:bg-purple-700 text-lg px-8 py-6"
          >
            <Zap size={20} />
            Criar meu portfólio grátis
            <ArrowRight size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MeuTrampoDemo;
